// Shape of GET /api/billing-dashboard/. Every amount is in euros, tax excluded.

export type Granularity = 'day' | 'week' | 'month';

export interface ChannelTotals {
  amount: number;
  orders: number;
  avg_ticket: number;
}

export interface OrdersAmount {
  amount: number;
  orders: number;
}

// Subscription `amount`, `orders` and `avg_ticket` add up what has been charged
// (locked or later) and what is still pending (confirmed).
export interface SubscriptionTotals extends ChannelTotals {
  charged: OrdersAmount;
  pending: OrdersAmount;
}

// B2B `amount` is net of credit notes; `orders` and `avg_ticket` only count
// invoices.
export interface B2BTotals extends ChannelTotals {
  credit_notes: { count: number; amount: number };
}

export interface BillingTotals {
  subscription: SubscriptionTotals;
  b2b: B2BTotals;
  total: { amount: number; orders: number };
}

export interface BillingPoint {
  // First day of the bucket (YYYY-MM-DD); weeks start on Monday.
  period: string;
  subscription: number;
  subscription_charged: number;
  subscription_pending: number;
  b2b: number;
  total: number;
}

export interface CountryBilling {
  country: string | null;
  subscription: number;
  subscription_charged: number;
  subscription_pending: number;
  b2b: number;
  total: number;
}

export interface BillingDashboard {
  generated_at: string;
  since: string;
  until: string;
  granularity: Granularity;
  totals: BillingTotals;
  // As many whole months right before; null unless the range is made of whole
  // months.
  previous_period: {
    since: string;
    until: string;
    totals: BillingTotals;
  } | null;
  series: BillingPoint[];
  by_country: CountryBilling[];
}

// Relative change against the previous period, or null when there is nothing
// to compare with.
export function variation(current: number, previous: number): number | null {
  if (!previous) return null;
  return (current - previous) / Math.abs(previous);
}

// Share of `amount` in `total`, or null when the total is not positive (a
// range made only of credit notes).
export function share(amount: number, total: number): number | null {
  return total > 0 ? amount / total : null;
}
